import apiClient, { type PagedResponse } from '../client'
import type { CompanyPersonResponse, CompanyDocumentResponse, DocumentRequirementResponse } from '../models/response'
import type { ProveedorResponse, ProveedorListItemResponse, MiningRightResponse, MiningRightComponentResponseItem, BankAccountResponse, IgafomRecordResponse, LegalDocumentRecordResponse, ProductionDeclarationResponse, TaxReportResponse, PowerValidityResponse, ObservationResponse, CuidResponse, SupplierApprovalListItemResponse, MinerVerificationResponse, StepApprovalEntry, ExplosiveAuthorizationResponse, ExplosiveHandlerResponse, SucamecManipuladorResponse, SupplierContractResponse, ShareholderResponse, ShareholderCertificateValidationResponse, RegistrationStatusChangeResponse } from '../models/response/proveedor'
import type { RestrictionCheckSummary, UpdatePolygonResponseBody, IngemmetImportResult, IngemmetExpedienteStatus } from '../models/response/restrictionCheck'
import type { ConsultarRucResponse } from '../models/response/consultarRuc'
import type { CreateProveedorStep1Request, CreateMiningRightRequest, UpdateAccreditationRequest, AddIgafomRecordRequest, AddLegalDocumentRecordRequest, AddProductionDeclarationRequest, AddTaxReportRequest, AddPowerValidityRequest, UpdateFinanceRequest, AddObservationRequest, AddExplosiveAuthorizationRequest, AddExplosiveHandlerRequest, AddShareholderRequest } from '../models/request/proveedor'
import type { AddCompanyPersonRequest } from '../models/request/company'

const BASE_PATH = '/proveedores'

export const proveedoresService = {
  async consultarRuc(ruc: string): Promise<ConsultarRucResponse> {
    return apiClient.get<ConsultarRucResponse>(`${BASE_PATH}/consultar-ruc/${ruc}`)
  },

  async getAll(params?: {
    page?: number
    pageSize?: number
    search?: string
  }): Promise<PagedResponse<ProveedorListItemResponse>> {
    return apiClient.get<PagedResponse<ProveedorListItemResponse>>(BASE_PATH, params)
  },

  // Directorio completo sin paginar, para exportar. El filtro de busqueda va al servidor:
  // exportar "todo" tiene que respetar lo que el usuario esta filtrando, no ignorarlo.
  async exportAll(search?: string): Promise<ProveedorListItemResponse[]> {
    const res = await apiClient.get<PagedResponse<ProveedorListItemResponse>>(`${BASE_PATH}/export`, { search })
    return res.items
  },

  async getById(id: string): Promise<ProveedorResponse> {
    return apiClient.get<ProveedorResponse>(`${BASE_PATH}/${id}`)
  },

  async getMinerVerifications(id: string): Promise<MinerVerificationResponse[]> {
    return apiClient.get<MinerVerificationResponse[]>(`${BASE_PATH}/${id}/miner-verifications`)
  },

  // Builds a URL to download an evidence/acta file via the generic programaciones file endpoint.
  getVerificationFileUrl(storagePath: string): string {
    return apiClient.buildUrl(`/programaciones/files/${storagePath}`)
  },

  async getPending(): Promise<ProveedorResponse> {
    return apiClient.get<ProveedorResponse>(`${BASE_PATH}/pending`)
  },

  async addPerson(id: string, data: AddCompanyPersonRequest): Promise<CompanyPersonResponse> {
    return apiClient.post<CompanyPersonResponse, AddCompanyPersonRequest>(`${BASE_PATH}/${id}/step2/persons`, data)
  },

  async removePerson(id: string, companyPersonId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${id}/step2/persons/${companyPersonId}`)
  },

  async uploadDocument(id: string, formData: FormData): Promise<CompanyDocumentResponse> {
    return apiClient.postForm<CompanyDocumentResponse>(`${BASE_PATH}/${id}/step5/documents`, formData)
  },

  async removeDocument(companyId: string, documentId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${companyId}/documents/${documentId}`)
  },

  getDocumentDownloadUrl(companyId: string, documentId: string): string {
    return apiClient.buildUrl(`${BASE_PATH}/${companyId}/documents/${documentId}/download`)
  },

  // ── Mining Right detail endpoints ──

  async updatePolygon(companyId: string, miningRightId: string, data: { vertices: { northing: number; easting: number }[]; utmZone: number }): Promise<UpdatePolygonResponseBody> {
    return apiClient.put<UpdatePolygonResponseBody>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/polygon`, data)
  },

  async getRestrictionCheck(companyId: string, miningRightId: string): Promise<RestrictionCheckSummary | null> {
    return apiClient.get<RestrictionCheckSummary | null>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/restriction-check`)
  },

  // Trae de INGEMMET los datos oficiales del derecho y, si no tiene, su poligono.
  // overwriteBoundary queda en false: el poligono cargado no se pisa desde aca.
  async importFromIngemmet(companyId: string, miningRightId: string, overwriteBoundary = false): Promise<IngemmetImportResult> {
    return apiClient.post<IngemmetImportResult>(
      `${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/ingemmet-import?overwriteBoundary=${overwriteBoundary}`)
  },

  // Empuja un lote de la descarga del expediente digitalizado. Se llama repetidamente
  // hasta que status sea Completado: cada llamada baja 40 paginas y vuelve enseguida.
  async advanceIngemmetExpediente(companyId: string, miningRightId: string): Promise<IngemmetExpedienteStatus> {
    return apiClient.post<IngemmetExpedienteStatus>(
      `${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/ingemmet-expediente`)
  },

  // Re-ejecuta el screening contra el polígono vigente y el catálogo actual de zonas.
  async recheckRestrictions(companyId: string, miningRightId: string): Promise<RestrictionCheckSummary | null> {
    return apiClient.post<RestrictionCheckSummary | null>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/restriction-check`)
  },

  async addComponent(companyId: string, miningRightId: string, data: { componentTypeId: string; name?: string; northing: number; easting: number }): Promise<MiningRightComponentResponseItem> {
    return apiClient.post<MiningRightComponentResponseItem>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/components`, data)
  },

  async removeComponent(companyId: string, miningRightId: string, componentId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/components/${componentId}`)
  },

  async addBankAccount(companyId: string, data: { accountNumber: string; bankEntityId: number; accountTypeId: number }): Promise<BankAccountResponse> {
    return apiClient.post<BankAccountResponse>(`${BASE_PATH}/${companyId}/bank-accounts`, data)
  },

  async removeBankAccount(companyId: string, accountId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${companyId}/bank-accounts/${accountId}`)
  },

  async getDocumentRequirements(): Promise<DocumentRequirementResponse[]> {
    return apiClient.get<DocumentRequirementResponse[]>(`${BASE_PATH}/document-requirements`)
  },

  // ── IGAFOM CRUD ──
  async addIgafomRecord(companyId: string, miningRightId: string, data: AddIgafomRecordRequest, receiptFile?: File, documentFile?: File): Promise<IgafomRecordResponse> {
    const formData = new FormData()
    if (data.presentationDate) formData.append('presentationDate', data.presentationDate)
    if (data.expirationDate) formData.append('expirationDate', data.expirationDate)
    if (data.fileNumber) formData.append('fileNumber', data.fileNumber)
    if (data.type) formData.append('type', String(data.type))
    if (data.exploitationMethod) formData.append('exploitationMethod', String(data.exploitationMethod))
    if (data.documentStatus) formData.append('documentStatus', String(data.documentStatus))
    if (data.substanceId) formData.append('substanceId', String(data.substanceId))
    if (data.productiveCapacity) formData.append('productiveCapacity', data.productiveCapacity)
    if (receiptFile) formData.append('receiptFile', receiptFile)
    if (documentFile) formData.append('documentFile', documentFile)
    return apiClient.postForm<IgafomRecordResponse>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/igafom`, formData)
  },

  async updateIgafomRecord(companyId: string, miningRightId: string, igafomId: string, data: AddIgafomRecordRequest, receiptFile?: File, documentFile?: File): Promise<IgafomRecordResponse> {
    const formData = new FormData()
    if (data.presentationDate) formData.append('presentationDate', data.presentationDate)
    if (data.expirationDate) formData.append('expirationDate', data.expirationDate)
    if (data.fileNumber) formData.append('fileNumber', data.fileNumber)
    if (data.type) formData.append('type', String(data.type))
    if (data.exploitationMethod) formData.append('exploitationMethod', String(data.exploitationMethod))
    if (data.documentStatus) formData.append('documentStatus', String(data.documentStatus))
    if (data.substanceId) formData.append('substanceId', String(data.substanceId))
    if (data.productiveCapacity) formData.append('productiveCapacity', data.productiveCapacity)
    if (receiptFile) formData.append('receiptFile', receiptFile)
    if (documentFile) formData.append('documentFile', documentFile)
    return apiClient.putForm<IgafomRecordResponse>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/igafom/${igafomId}`, formData)
  },

  async removeIgafomRecord(companyId: string, miningRightId: string, igafomId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/igafom/${igafomId}`)
  },

  // ── OTROS DOCUMENTOS LEGALES CRUD (observación 13) ──
  async addLegalDocumentRecord(companyId: string, miningRightId: string, data: AddLegalDocumentRecordRequest, receiptFile?: File, documentFile?: File): Promise<LegalDocumentRecordResponse> {
    const formData = new FormData()
    if (data.documentType) formData.append('documentType', String(data.documentType))
    if (data.presentationDate) formData.append('presentationDate', data.presentationDate)
    if (data.expirationDate) formData.append('expirationDate', data.expirationDate)
    if (data.fileNumber) formData.append('fileNumber', data.fileNumber)
    if (data.documentStatus) formData.append('documentStatus', String(data.documentStatus))
    if (receiptFile) formData.append('receiptFile', receiptFile)
    if (documentFile) formData.append('documentFile', documentFile)
    return apiClient.postForm<LegalDocumentRecordResponse>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/legal-documents`, formData)
  },

  async updateLegalDocumentRecord(companyId: string, miningRightId: string, legalDocumentId: string, data: AddLegalDocumentRecordRequest, receiptFile?: File, documentFile?: File): Promise<LegalDocumentRecordResponse> {
    const formData = new FormData()
    if (data.documentType) formData.append('documentType', String(data.documentType))
    if (data.presentationDate) formData.append('presentationDate', data.presentationDate)
    if (data.expirationDate) formData.append('expirationDate', data.expirationDate)
    if (data.fileNumber) formData.append('fileNumber', data.fileNumber)
    if (data.documentStatus) formData.append('documentStatus', String(data.documentStatus))
    if (receiptFile) formData.append('receiptFile', receiptFile)
    if (documentFile) formData.append('documentFile', documentFile)
    return apiClient.putForm<LegalDocumentRecordResponse>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/legal-documents/${legalDocumentId}`, formData)
  },

  async removeLegalDocumentRecord(companyId: string, miningRightId: string, legalDocumentId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/legal-documents/${legalDocumentId}`)
  },

  getIgafomFileDownloadUrl(objectName: string): string {
    return apiClient.buildUrl(`${BASE_PATH}/igafom-download?objectName=${encodeURIComponent(objectName)}`)
  },

  // Descarga genérica de un archivo del bucket por objectName (contratos, IGAFOM, legales,
  // SPLAFT, explosivos, etc.). El endpoint 'igafom-download' es un nombre legacy: en realidad
  // sirve cualquier archivo. Requiere [Authorize], y el navegador no envía el token en un
  // <a href> directo (→ 401), así que la descarga se hace con fetch + Bearer + blob.
  async downloadFile(objectName: string, fileName?: string): Promise<void> {
    const url = apiClient.buildUrl(`${BASE_PATH}/igafom-download?objectName=${encodeURIComponent(objectName)}`)
    const response = await apiClient.fetchAuthorized(url)
    if (!response.ok) throw new Error('Error descargando archivo')
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = fileName || objectName.split('/').pop() || 'documento.pdf'
    a.click()
    URL.revokeObjectURL(blobUrl)
  },

  async downloadCertificate(objectName: string): Promise<void> {
    const url = apiClient.buildUrl(`${BASE_PATH}/certificate-download?objectName=${encodeURIComponent(objectName)}`)
    const response = await apiClient.fetchAuthorized(url)
    if (!response.ok) throw new Error('Error descargando certificado')
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = 'certificado-cuid.pdf'
    a.click()
    URL.revokeObjectURL(blobUrl)
  },

  // Paso 5 - Producción
  async addProductionDeclaration(companyId: string, miningRightId: string, data: AddProductionDeclarationRequest, documentFile?: File): Promise<ProductionDeclarationResponse> {
    const formData = new FormData()
    if (data.period) formData.append('period', String(data.period))
    if (data.year) formData.append('year', String(data.year))
    if (data.dailyEstimatedProduction) formData.append('dailyEstimatedProduction', String(data.dailyEstimatedProduction))
    if (data.presentationDate) formData.append('presentationDate', data.presentationDate)
    if (data.fileNumber) formData.append('fileNumber', data.fileNumber)
    if (data.totalMetallic) formData.append('totalMetallic', String(data.totalMetallic))
    if (data.totalNonMetallic) formData.append('totalNonMetallic', String(data.totalNonMetallic))
    if (data.expirationDate) formData.append('expirationDate', data.expirationDate)
    if (documentFile) formData.append('documentFile', documentFile)
    return apiClient.postForm<ProductionDeclarationResponse>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/production`, formData)
  },

  async removeProductionDeclaration(companyId: string, miningRightId: string, declarationId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/production/${declarationId}`)
  },

  // Paso 8 - Reportes Tributarios
  async addTaxReport(companyId: string, data: AddTaxReportRequest, documentFile: File): Promise<TaxReportResponse> {
    const formData = new FormData()
    if (data.code) formData.append('code', data.code)
    formData.append('issueDate', data.issueDate)
    formData.append('documentFile', documentFile)
    return apiClient.postForm<TaxReportResponse>(`${BASE_PATH}/${companyId}/tax-reports`, formData)
  },

  async removeTaxReport(companyId: string, reportId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${companyId}/tax-reports/${reportId}`)
  },

  // Vigencia de Poder (ligada al CompanyPerson Representante Legal)
  async addPowerValidity(companyId: string, companyPersonId: string, data: AddPowerValidityRequest, documentFile: File): Promise<PowerValidityResponse> {
    const formData = new FormData()
    formData.append('code', data.code)
    formData.append('issueDate', data.issueDate)
    formData.append('documentFile', documentFile)
    return apiClient.postForm<PowerValidityResponse>(`${BASE_PATH}/${companyId}/company-persons/${companyPersonId}/power-validities`, formData)
  },

  async removePowerValidity(companyId: string, powerValidityId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${companyId}/power-validities/${powerValidityId}`)
  },

  // Evaluación - Observaciones
  async addObservation(id: string, data: AddObservationRequest): Promise<ObservationResponse> {
    return apiClient.post<ObservationResponse>(`${BASE_PATH}/${id}/observations`, data)
  },

  async resolveObservation(id: string, observationId: string): Promise<void> {
    return apiClient.put<void>(`${BASE_PATH}/${id}/observations/${observationId}/resolve`)
  },

  async addObservationReply(id: string, observationId: string, text: string): Promise<void> {
    return apiClient.post<void, { replyText: string }>(`${BASE_PATH}/${id}/observations/${observationId}/replies`, { replyText: text })
  },

  // Paso 10 - Autorización
  async authorize(id: string): Promise<CuidResponse> {
    return apiClient.post<CuidResponse>(`${BASE_PATH}/${id}/authorize`)
  },

  async getApprovals(id: string): Promise<SupplierApprovalListItemResponse[]> {
    return apiClient.get<SupplierApprovalListItemResponse[]>(`${BASE_PATH}/${id}/approvals`)
  },

  /**
   * ZIP del expediente del minero. Antes se mandaba la ExpedienteUrl
   * ("/api/expedientes/{code}/download") como objectName a certificate-download —que la
   * buscaba en el bucket y fallaba— y el archivo se nombraba "expediente.pdf" siendo un ZIP.
   */
  async downloadExpediente(cuid: { expedienteCode: string | null; expedienteUrl: string | null }): Promise<void> {
    const code = cuid.expedienteCode ?? cuid.expedienteUrl?.match(/expedientes\/([^/]+)\/download/)?.[1]
    if (!code) throw new Error('El minero no tiene expediente')
    const url = apiClient.buildUrl(`/expedientes/${encodeURIComponent(decodeURIComponent(code))}/download`)
    const response = await apiClient.fetchAuthorized(url)
    if (!response.ok) throw new Error('Error descargando expediente')
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = `${code}.zip`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(blobUrl)
  },

  // ── Step save endpoints (1 endpoint per step) ──

  async saveStepIdentity(
    data: CreateProveedorStep1Request,
    // pendingPowerValidityFiles: each file matches a pendingPowerValidities
    // entry in `data` by index — we wire them as `pv_file_0`, `pv_file_1`,
    // and the JSON references them via `fileKey`. Backend pulls the matching
    // IFormFile from Request.Form.Files.
    pendingPowerValidityFiles?: File[],
  ): Promise<ProveedorResponse> {
    const formData = new FormData()
    formData.append('request', JSON.stringify(data))
    pendingPowerValidityFiles?.forEach((file, idx) => {
      formData.append(`pv_file_${idx}`, file)
    })
    return apiClient.postForm<ProveedorResponse>(`${BASE_PATH}/step/identity`, formData)
  },

  async downloadConsentTemplate(companyId: string, ruc: string): Promise<void> {
    const url = apiClient.buildUrl(`${BASE_PATH}/${companyId}/consent-template`)
    const response = await apiClient.fetchAuthorized(url)
    if (!response.ok) {
      let message = 'Error descargando la plantilla de consentimiento'
      try {
        const body = await response.json()
        if (body?.message) message = body.message
      } catch { /* keep default */ }
      throw new Error(message)
    }
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = `Consentimiento-${ruc}.pdf`
    a.click()
    URL.revokeObjectURL(blobUrl)
  },

  async saveStepConsent(id: string, consentFile: File): Promise<void> {
    const formData = new FormData()
    formData.append('consentFile', consentFile)
    return apiClient.postForm<void>(`${BASE_PATH}/${id}/step/consent`, formData)
  },

  async requestConsentReacceptance(id: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/consent/request-reacceptance`)
  },

  async saveStepAccreditation(id: string, data: UpdateAccreditationRequest): Promise<void> {
    return apiClient.put<void>(`${BASE_PATH}/${id}/step/accreditation`, data)
  },

  async saveStepMiningRights(id: string, data: CreateMiningRightRequest[]): Promise<MiningRightResponse[]> {
    return apiClient.put<MiningRightResponse[]>(`${BASE_PATH}/${id}/step/mining-rights`, data)
  },

  // ── Contrato ──
  async saveStepContract(id: string): Promise<void> {
    return apiClient.put<void>(`${BASE_PATH}/${id}/step/contract`)
  },

  async uploadContract(id: string, documentFile: File, contractDate?: string, notes?: string, expirationDate?: string): Promise<SupplierContractResponse> {
    const formData = new FormData()
    if (contractDate) formData.append('contractDate', contractDate)
    if (expirationDate) formData.append('expirationDate', expirationDate)
    if (notes) formData.append('notes', notes)
    formData.append('documentFile', documentFile)
    return apiClient.postForm<SupplierContractResponse>(`${BASE_PATH}/${id}/contract`, formData)
  },

  async saveStepIgafom(id: string): Promise<void> {
    return apiClient.put<void>(`${BASE_PATH}/${id}/step/igafom`)
  },

  async saveStepProduction(id: string): Promise<void> {
    return apiClient.put<void>(`${BASE_PATH}/${id}/step/production`)
  },

  async saveStepRecpo(id: string): Promise<void> {
    return apiClient.put<void>(`${BASE_PATH}/${id}/step/recpo`)
  },

  async saveStepIqbf(id: string): Promise<void> {
    return apiClient.put<void>(`${BASE_PATH}/${id}/step/iqbf`)
  },

  // ── IQBF/Explosivos: Autorizaciones por Derecho Minero ──
  async setMiningRightUsesExplosives(companyId: string, miningRightId: string, usesExplosives: boolean): Promise<void> {
    return apiClient.put<void>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/uses-explosives`, { usesExplosives })
  },

  async addExplosiveAuthorization(companyId: string, miningRightId: string, data: AddExplosiveAuthorizationRequest, documentFile?: File): Promise<ExplosiveAuthorizationResponse> {
    const formData = new FormData()
    formData.append('authorizationType', String(data.authorizationType))
    if (data.resolutionNumber) formData.append('resolutionNumber', data.resolutionNumber)
    if (data.emissionDate) formData.append('emissionDate', data.emissionDate)
    if (data.expirationDate) formData.append('expirationDate', data.expirationDate)
    if (data.authorizedExplosives) formData.append('authorizedExplosives', data.authorizedExplosives)
    if (data.maxCapacity) formData.append('maxCapacity', data.maxCapacity)
    if (documentFile) formData.append('documentFile', documentFile)
    return apiClient.postForm<ExplosiveAuthorizationResponse>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/explosive-authorizations`, formData)
  },

  async updateExplosiveAuthorization(companyId: string, miningRightId: string, authId: string, data: AddExplosiveAuthorizationRequest, documentFile?: File): Promise<ExplosiveAuthorizationResponse> {
    const formData = new FormData()
    formData.append('authorizationType', String(data.authorizationType))
    if (data.resolutionNumber) formData.append('resolutionNumber', data.resolutionNumber)
    if (data.emissionDate) formData.append('emissionDate', data.emissionDate)
    if (data.expirationDate) formData.append('expirationDate', data.expirationDate)
    if (data.authorizedExplosives) formData.append('authorizedExplosives', data.authorizedExplosives)
    if (data.maxCapacity) formData.append('maxCapacity', data.maxCapacity)
    if (documentFile) formData.append('documentFile', documentFile)
    return apiClient.putForm<ExplosiveAuthorizationResponse>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/explosive-authorizations/${authId}`, formData)
  },

  async removeExplosiveAuthorization(companyId: string, miningRightId: string, authId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/explosive-authorizations/${authId}`)
  },

  // ── IQBF/Explosivos: Manipuladores por Derecho Minero ──
  async addExplosiveHandler(companyId: string, miningRightId: string, data: AddExplosiveHandlerRequest): Promise<ExplosiveHandlerResponse> {
    return apiClient.post<ExplosiveHandlerResponse, AddExplosiveHandlerRequest>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/explosive-handlers`, data)
  },

  async bulkAddExplosiveHandlers(companyId: string, miningRightId: string, items: AddExplosiveHandlerRequest[]): Promise<ExplosiveHandlerResponse[]> {
    return apiClient.post<ExplosiveHandlerResponse[], AddExplosiveHandlerRequest[]>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/explosive-handlers/bulk`, items)
  },

  async updateExplosiveHandler(companyId: string, miningRightId: string, handlerId: string, data: AddExplosiveHandlerRequest): Promise<ExplosiveHandlerResponse> {
    return apiClient.put<ExplosiveHandlerResponse, AddExplosiveHandlerRequest>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/explosive-handlers/${handlerId}`, data)
  },

  async removeExplosiveHandler(companyId: string, miningRightId: string, handlerId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${companyId}/mining-rights/${miningRightId}/explosive-handlers/${handlerId}`)
  },

  async consultarSucamecManipulador(dni: string): Promise<SucamecManipuladorResponse> {
    return apiClient.get<SucamecManipuladorResponse>(`${BASE_PATH}/sucamec/manipulador/${encodeURIComponent(dni)}`)
  },

  // ── Accionistas y Beneficiarios Finales (UBO) — solo S.A./S.A.C. ──
  async addShareholder(companyId: string, data: AddShareholderRequest, certificateFile?: File): Promise<ShareholderResponse> {
    const fd = new FormData()
    fd.append('documentType', String(data.documentType))
    fd.append('documentNumber', data.documentNumber)
    fd.append('firstName', data.firstName)
    fd.append('lastName', data.lastName)
    fd.append('participationPercent', String(data.participationPercent))
    if (certificateFile) fd.append('certificateFile', certificateFile)
    return apiClient.postForm<ShareholderResponse>(`${BASE_PATH}/${companyId}/shareholders`, fd)
  },

  async updateShareholder(companyId: string, shareholderId: string, data: AddShareholderRequest, certificateFile?: File): Promise<ShareholderResponse> {
    const fd = new FormData()
    fd.append('documentType', String(data.documentType))
    fd.append('documentNumber', data.documentNumber)
    fd.append('firstName', data.firstName)
    fd.append('lastName', data.lastName)
    fd.append('participationPercent', String(data.participationPercent))
    if (certificateFile) fd.append('certificateFile', certificateFile)
    return apiClient.putForm<ShareholderResponse>(`${BASE_PATH}/${companyId}/shareholders/${shareholderId}`, fd)
  },

  async removeShareholder(companyId: string, shareholderId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${companyId}/shareholders/${shareholderId}`)
  },

  // Alta "PDF primero": sube el Certificado Único Laboral; el backend lee el DNI,
  // resuelve el nombre por RENIEC (crea la Persona) y valida el certificado.
  async addShareholderFromCertificate(companyId: string, certificateFile: File, participationPercent: number): Promise<ShareholderResponse> {
    const fd = new FormData()
    fd.append('participationPercent', String(participationPercent))
    fd.append('certificateFile', certificateFile)
    return apiClient.postForm<ShareholderResponse>(`${BASE_PATH}/${companyId}/shareholders/from-certificate`, fd)
  },

  async saveStepShareholders(id: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/step-shareholders`)
  },

  // Staging (solicitud de modificación): sube y valida un CUL sin persistir
  // accionista; devuelve además la identidad resuelta (DNI + nombre por RENIEC).
  async validateShareholderCertificate(companyId: string, certificateFile: File): Promise<ShareholderCertificateValidationResponse> {
    const fd = new FormData()
    fd.append('certificateFile', certificateFile)
    return apiClient.postForm<ShareholderCertificateValidationResponse>(`${BASE_PATH}/${companyId}/shareholders/validate-certificate`, fd)
  },

  async saveStepFinance(id: string, data: UpdateFinanceRequest, splaftFile?: File): Promise<void> {
    const formData = new FormData()
    formData.append('splaftDeclared', String(data.splaftDeclared))
    if (data.incomeTaxCategoryId) formData.append('incomeTaxCategoryId', String(data.incomeTaxCategoryId))
    if (splaftFile) formData.append('splaftFile', splaftFile)
    return apiClient.putForm<void>(`${BASE_PATH}/${id}/step/finance`, formData)
  },

  async saveStepFinalize(id: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/step/finalize`)
  },

  async submitForEvaluation(id: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/submit-for-evaluation`)
  },

  // ── Per-Step Approval (Task 11) ──

  async approveStep(id: string, step: number): Promise<StepApprovalEntry> {
    return apiClient.post<StepApprovalEntry>(`${BASE_PATH}/${id}/steps/${step}/approve`)
  },

  async getStepApprovals(id: string): Promise<StepApprovalEntry[]> {
    return apiClient.get<StepApprovalEntry[]>(`${BASE_PATH}/${id}/step-approvals`)
  },

  async getStatusHistory(id: string): Promise<RegistrationStatusChangeResponse[]> {
    return apiClient.get<RegistrationStatusChangeResponse[]>(`${BASE_PATH}/${id}/status-history`)
  },
}
